import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ShoppingCart, Eye, Download, Star, Filter, Search } from 'lucide-react'
import { useCartStore } from '../store/cartStore'
import toast from 'react-hot-toast'

interface Template {
  id: number
  title: string
  slug: string
  description: string
  price: number
  discountPrice?: number
  thumbnailUrl: string
  rating: number
  downloadCount: number
  reviewCount: number
  features: string
  isActive: boolean
  createdAt: string
}

const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [sortBy, setSortBy] = useState('newest')
  const [priceRange, setPriceRange] = useState('all')
  
  const { addToCart } = useCartStore()

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://cloudflare-ecommerce-api.hilal-b8d.workers.dev/api'
      const response = await fetch(`${API_BASE_URL}/products?category=blogger-templates`)
      if (response.ok) {
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json()
          setTemplates(data.products || [])
        } else {
          console.error('Response is not JSON:', await response.text())
          toast.error('Server mengembalikan response yang tidak valid')
        }
      } else {
        console.error('Response not OK:', response.status, response.statusText)
        toast.error(`Error: ${response.status} - ${response.statusText}`)
      }
    } catch (error) {
      console.error('Error fetching templates:', error)
      toast.error('Gagal memuat template')
    } finally {
      setLoading(false)
    }
  }

  const handleAddToCart = async (template: Template) => {
    const success = await addToCart(template.id, 1)
    if (success) {
      // Toast sudah ditampilkan di store
    }
  }

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesPrice = priceRange === 'all' || 
                        (priceRange === 'under-100k' && template.price < 100000) ||
                        (priceRange === '100k-200k' && template.price >= 100000 && template.price <= 200000) ||
                        (priceRange === 'over-200k' && template.price > 200000)
    
    return matchesSearch && matchesPrice
  })

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price
      case 'price-high':
        return b.price - a.price
      case 'popular':
        return b.downloadCount - a.downloadCount
      case 'rating':
        return b.rating - a.rating
      default: // newest
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    }
  })

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="h-48 bg-gray-200 rounded mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            🎨 Template Blogger Premium
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Koleksi template Blogger premium dengan desain modern, responsive, dan SEO optimized. 
            Sempurna untuk blog profesional Anda.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Cari template..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Price Range */}
            <select
              value={priceRange}
              onChange={(e) => setPriceRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Semua Harga</option>
              <option value="under-100k">Di bawah 100k</option>
              <option value="100k-200k">100k - 200k</option>
              <option value="over-200k">Di atas 200k</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="newest">Terbaru</option>
              <option value="popular">Terpopuler</option>
              <option value="rating">Rating Tertinggi</option>
              <option value="price-low">Harga Terendah</option>
              <option value="price-high">Harga Tertinggi</option>
            </select>

            {/* Results Count */}
            <div className="flex items-center text-gray-600">
              <Filter className="w-5 h-5 mr-2" />
              {sortedTemplates.length} template ditemukan
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        {sortedTemplates.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">Tidak ada template ditemukan</h3>
            <p className="text-gray-600">Coba ubah filter pencarian Anda</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sortedTemplates.map((template) => (
              <div key={template.id} className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                {/* Template Preview */}
                <div className="relative group">
                  <img
                    src={template.thumbnailUrl}
                    alt={template.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center">
                    <Link
                      to={`/products/${template.slug}`}
                      className="opacity-0 group-hover:opacity-100 bg-white text-gray-900 px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Lihat Detail
                    </Link>
                  </div>
                  
                  {/* Badge */}
                  {template.slug.includes('bundle') && (
                    <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      HEMAT 30%
                    </div>
                  )}
                </div>

                {/* Template Info */}
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {template.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {template.description}
                  </p>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span>{template.rating.toFixed(1)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Download className="w-4 h-4" />
                      <span>{template.downloadCount}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      <span>{template.reviewCount}</span>
                    </div>
                  </div>

                  {/* Features */}
                  {template.features && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {template.features.split('|').slice(0, 3).map((feature, index) => (
                          <span
                            key={index}
                            className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full"
                          >
                            {feature.trim()}
                          </span>
                        ))}
                        {template.features.split('|').length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{template.features.split('|').length - 3} lainnya
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Price & Action */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {formatPrice(template.price)}
                      </div>
                      {template.slug.includes('bundle') && (
                        <div className="text-sm text-gray-500 line-through">
                          {formatPrice(278000)}
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleAddToCart(template)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Beli
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            🚀 Butuh Template Custom?
          </h2>
          <p className="text-xl mb-6 opacity-90">
            Kami juga menyediakan jasa pembuatan template blogger custom sesuai kebutuhan Anda
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
          >
            Hubungi Kami
          </Link>
        </div>
      </div>
    </div>
  )
}

export default TemplatesPage
